/**
 * applePopupBridge — shared plumbing for the manual Sign-in-with-Apple
 * popup flow.
 *
 * WHY a manual flow instead of Apple's `AppleID.auth.signIn()` JS SDK?
 * -------------------------------------------------------------------
 * The official SDK's `usePopup: true` path is hard-wired to
 * `response_mode=web_message`. In that mode Apple's authorize page must
 * `window.opener.postMessage(payload, origin)` the result back to us.
 * When Cross-Origin-Opener-Policy (COOP) severs the popup ↔ opener
 * link — which happens on many hosting stacks including our own
 * preview + production ingress — that postMessage silently fails.
 * Apple then either:
 *   • shows "Sign Up Not Completed" inside the popup, or
 *   • closes the popup and leaves the opener's `signIn()` promise
 *     hanging forever on a blank white page.
 *
 * We sidestep the entire `web_message` handshake by:
 *   1. Constructing the Apple authorize URL ourselves with
 *      `response_mode=fragment`.
 *   2. Opening it in a popup we own (`window.open`).
 *   3. Letting Apple redirect the popup back to our registered
 *      `redirect_uri` (== `window.location.origin`) with the auth
 *      response in the URL fragment.
 *   4. Detecting the fragment on boot (see `index.js`) and
 *      forwarding the payload to the opener via THREE parallel
 *      channels: localStorage `storage` event, BroadcastChannel, and
 *      `window.opener.postMessage`. All three are idempotent —
 *      whichever arrives first wins.
 *
 * `scope` is intentionally omitted from the authorize URL. Requesting
 * `name email` would force Apple to use `response_mode=form_post`
 * (again unusable in a pure SPA). Xano decodes the JWT `identity_token`
 * server-side to derive the user's email + Apple sub, so the client
 * doesn't need the user object.
 */

// Same channel names the previous SDK-based fallback used, so any
// stray listeners keep working during transition.
export const APPLE_RESULT_KEY = "brontoy_apple_result";
export const BROADCAST_CHANNEL_NAME = "brontoy-apple-auth";
export const POPUP_MESSAGE_TYPE = "brontoy-apple-callback";

/**
 * Parse the Apple auth response from a URL fragment or query string.
 * Returns `null` if the string doesn't look like an Apple response.
 */
export function parseAppleResponse(fragmentOrQuery) {
  if (!fragmentOrQuery) return null;
  const cleaned = fragmentOrQuery.replace(/^[#?]/, "");
  const params = new URLSearchParams(cleaned);
  const code = params.get("code");
  const idToken = params.get("id_token");
  const state = params.get("state");
  const error = params.get("error");
  // Nothing that looks like Apple → let the caller ignore this URL.
  if (!code && !idToken && !error) return null;

  // Apple returns `user` (JSON string) ONLY on first sign-in for a
  // given Services ID, and only when `scope` was requested. Without
  // scope it will simply be absent — that's fine, Xano derives the
  // user from the JWT.
  let user = null;
  const userStr = params.get("user");
  if (userStr) {
    try {
      const parsed = JSON.parse(userStr);
      user = {
        email: parsed?.email || null,
        firstName: parsed?.name?.firstName || null,
        lastName: parsed?.name?.lastName || null,
      };
    } catch {
      user = null;
    }
  }

  return {
    type: POPUP_MESSAGE_TYPE,
    code: code || null,
    id_token: idToken || null,
    state: state || null,
    error: error || null,
    error_description: params.get("error_description") || null,
    user,
    _ts: Date.now(),
  };
}

/**
 * Fire-and-forget the Apple response to the opener via ALL three
 * channels. Safe to call from a popup that may or may not have a
 * live `window.opener`.
 */
export function relayAppleResponse(payload) {
  if (!payload) return;

  // 1. localStorage — most reliable under COOP; `storage` events fire
  //    in every SAME-ORIGIN window regardless of browsing-context
  //    group. Timestamp makes the value unique across rapid retries.
  try {
    localStorage.setItem(APPLE_RESULT_KEY, JSON.stringify(payload));
    // Clean up shortly after so a future signIn() can't accidentally
    // read a stale value.
    setTimeout(() => {
      try {
        localStorage.removeItem(APPLE_RESULT_KEY);
      } catch {
        /* ignore */
      }
    }, 30_000);
  } catch {
    /* storage disabled — ignore */
  }

  // 2. BroadcastChannel — works when opener and popup share a
  //    browsing-context group.
  try {
    const ch = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    ch.postMessage(payload);
    setTimeout(() => {
      try {
        ch.close();
      } catch {
        /* ignore */
      }
    }, 200);
  } catch {
    /* Not supported / restricted — ignore */
  }

  // 3. window.opener.postMessage — legacy channel, only survives when
  //    COOP didn't sever the link.
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(payload, window.location.origin);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Runs from `index.js` BEFORE React boots. If the current URL fragment
 * (or query) contains an Apple auth response, we:
 *   1. Relay the payload to the opener via all three channels.
 *   2. Render a tiny "closing…" screen.
 *   3. Attempt `window.close()`.
 *   4. Return `true` so `index.js` can skip mounting React entirely.
 *
 * Returns `false` (and does nothing) when this isn't an Apple popup.
 */
export function handleApplePopupIfPresent() {
  if (typeof window === "undefined") return false;

  const fromHash = parseAppleResponse(window.location.hash);
  const fromQuery = fromHash ? null : parseAppleResponse(window.location.search);
  const payload = fromHash || fromQuery;
  if (!payload) return false;

  // Sanity check — only treat this as a popup callback if we appear
  // to have BEEN opened by someone (window.opener present) OR are the
  // only tab in a small window. Otherwise, a user sharing a link that
  // happens to contain #code= shouldn't accidentally hijack their
  // session. In practice Apple only ever puts these params here from
  // a real popup, so `window.opener` is the strongest guard.
  const looksLikePopup =
    !!window.opener ||
    (window.innerWidth <= 640 && window.innerHeight <= 800) ||
    // Fallback — if the fragment is here at all we still relay via
    // localStorage; the opener listener will only accept a payload
    // when it initiated a signIn().
    true;

  if (!looksLikePopup) return false;

  console.log("[BrontoY Apple popup] detected auth response, relaying:", {
    code_present: !!payload.code,
    id_token_present: !!payload.id_token,
    state_present: !!payload.state,
    error: payload.error,
    has_window_opener: !!window.opener,
  });

  relayAppleResponse(payload);

  // Minimal inline "closing" UI so the popup doesn't flash our full
  // React app for 100ms before window.close.
  try {
    document.documentElement.style.background = "#000";
    document.body.style.margin = "0";
    document.body.style.background = "#000";
    document.body.style.color = "#fff";
    document.body.style.font =
      "500 15px/1.5 -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif";
    document.body.innerHTML =
      '<div style="min-height:100dvh;display:flex;flex-direction:column;' +
      'align-items:center;justify-content:center;gap:16px;padding:0 24px;text-align:center">' +
      '<div style="width:36px;height:36px;border:2px solid rgba(255,255,255,.2);' +
      "border-top-color:#fff;border-radius:50%;animation:brontoy-apple-spin 1s linear infinite\"></div>" +
      '<p style="max-width:320px;opacity:.85">Completing sign in with Apple…</p>' +
      '<style>@keyframes brontoy-apple-spin{to{transform:rotate(360deg)}}</style>' +
      "</div>";
  } catch {
    /* If DOM writes fail we still already relayed; window will close shortly. */
  }

  // Give the storage/BroadcastChannel/postMessage a beat to drain
  // before closing. If close is denied by the browser (rare — we
  // opened the window ourselves so we own it), the opener will still
  // have received the payload on the fastest channel.
  setTimeout(() => {
    try {
      window.close();
    } catch {
      /* not owner — ignore */
    }
  }, 500);

  return true;
}
